/**
 * Issuer-agnostic role enum used everywhere the SPA gates UI on
 * authorization. Values mirror the names emitted by the
 * simpl-auth-authorizer Lambda (see `api/lib/auth-claims/index.ts`), which
 * normalizes both legacy Cognito groups (`SysAdmins`, `ProgramAdmins`) and
 * native simpl_auth claims into the same vocabulary. Using the same names
 * top-to-bottom means the SPA reads `caller.roles` directly during the
 * dual-issuer rollout and after the bridge ships.
 *
 * The legacy Cognito `Users` group is intentionally NOT modeled here.
 * It was a coarse "is this person a Trainee or Attending in some
 * program?" flag kept in sync with active program memberships by
 * `update-program-memberships`. SysAdmins were never added to it
 * automatically. The SPA now answers the same "can this person see and
 * use the Assessments flow?" question by reading
 * `currentUser.programMemberships` directly via
 * `hasActiveTraineeOrAttendingMembership` (see `app/utils.ts`),
 * mirroring the mobile clients' `isTrainee() || isAttending()` check.
 */
export enum Role {
  SystemAdmin = "SystemAdmin",
  ProgramAdmin = "ProgramAdmin"
}

/** Roles that grant any kind of admin access (used by the top-nav menu). */
export const ADMIN_ROLES: Role[] = [Role.SystemAdmin, Role.ProgramAdmin];

/**
 * Shape of `caller` returned at the top of `/get-user` responses since
 * Commit C. We only declare what the SPA actually reads — the backend
 * also returns `entitlements`, `sponsorIds`, `programIds`, and `issuer`
 * which we'll start consuming once the relevant features land.
 */
export type CallerClaims = {
  email: string;
  roles: string[];
  /**
   * simpl_auth's `simpl.org/roles` claim: the scopes each role is bound
   * to, e.g. `{ProgramAdmin: {program: [...]}, RotationDirector:
   * {person_group: [...]}}`. As of sign-in.
   */
  scopedRoles?: Record<string, Record<string, string[]>>;
  /**
   * SE-787: JWT-based impersonation trailer. Present when a SysAdmin
   * is impersonating another user through simpl_auth's /impersonate
   * flow. `email`/`roles` above are the TARGET user's; these two carry
   * the impersonator's real identity for the impersonation banner.
   * Both undefined when the caller is signed in as themselves.
   */
  impersonatedBy?: string;
  impersonatorRole?: string;
};

/**
 * Minimal shape of an active program-membership row that this module
 * needs to derive `Role.ProgramAdmin` from. The full `ProgramMembership`
 * type lives in `API.ts`; we duck-type here so unrelated changes to that
 * generated file don't ripple into role logic.
 */
type ProgramMembershipForRoles = {
  programRole?: string | null;
  endDate?: string | null;
} | null;

/**
 * Best-effort mapping of a server-emitted role string to the SPA `Role`
 * enum. Returns null for unknown roles so the caller can decide whether
 * to log/ignore them. Unknown roles are expected during forward-compat
 * windows (e.g. simpl_auth starts emitting a new role before the SPA is
 * updated).
 */
export function mapServerRole(serverRole: string): Role | null {
  switch (serverRole) {
    case "SystemAdmin":
      return Role.SystemAdmin;
    case "ProgramAdmin":
      return Role.ProgramAdmin;
    default:
      return null;
  }
}

/**
 * Maps a legacy Cognito group name to the SPA `Role` enum. Used only as
 * a fallback when `caller.roles` is missing — i.e. against an older
 * `/get-user` Lambda that was deployed before Commit C, or in error
 * paths where the User payload was not available.
 */
export function mapCognitoGroup(groupName: string): Role | null {
  switch (groupName) {
    case "SysAdmins":
      return Role.SystemAdmin;
    case "ProgramAdmins":
      return Role.ProgramAdmin;
    default:
      // The legacy `Users` Cognito group is intentionally dropped — see
      // the file header. `InstitutionalAdmins` and any other historical
      // group are also not used anywhere downstream of the SPA's
      // authorization checks.
      return null;
  }
}

/**
 * Builds the deduplicated `Role[]` for the Redux slice from a `caller`
 * payload (preferred), legacy Cognito group names (fallback), and the
 * caller's own simpl-api `programMembership` rows.
 *
 * Returns an empty array when there are no claims at all (signed out /
 * no session) and a possibly-empty array of admin roles otherwise — a
 * pure Trainee / Attending who has no admin grants will get back `[]`.
 * "Can this person use the Assessments flow?" is answered separately by
 * `hasActiveTraineeOrAttendingMembership` in `app/utils.ts`, which reads
 * the same program memberships rather than the role slice.
 *
 * Why `programMemberships` is a role source at all:
 *   `caller.roles` is derived by simpl_auth from `medqip.person_dim_v1`
 *   plus `simpl_auth.user_grants`. medqip's program assembly drops
 *   programs matching test/fake/demo (see medqip-etl 0150_program.sql
 *   `app.exclude_test_programs` flag, default ON in prod), and ETL
 *   latency can omit recently-invited users even for real programs. In
 *   both cases, simpl-api still has the authoritative
 *   `programMembership` row with `programRole === "ProgramAdmin"` —
 *   that's what set the user's Cognito `ProgramAdmins` group in the
 *   first place. Deriving `Role.ProgramAdmin` from those rows keeps the
 *   SPA's admin-tab gating consistent with simpl-api's own data, the
 *   same way `canAssess` already trusts the local memberships for
 *   Trainee / Attending gating. `Role.SystemAdmin` deliberately is NOT
 *   derived here: it's a global role with no per-program membership and
 *   lives correctly in `simpl_auth.user_grants`.
 */
export function rolesFromAuthUser(input: {
  caller?: CallerClaims | null;
  cognitoGroups?: string[];
  programMemberships?: ReadonlyArray<ProgramMembershipForRoles> | null;
}): Role[] {
  const callerRoles = input.caller?.roles ?? [];
  const cognitoGroups = input.cognitoGroups ?? [];
  const programMemberships = input.programMemberships ?? [];
  const hasAnyClaim =
    !!input.caller?.email ||
    callerRoles.length > 0 ||
    cognitoGroups.length > 0 ||
    programMemberships.length > 0;

  if (!hasAnyClaim) {
    return [];
  }

  const roles = new Set<Role>();
  for (const r of callerRoles) {
    const mapped = mapServerRole(r);
    if (mapped) roles.add(mapped);
  }
  // Only consult Cognito groups when caller.roles is absent — caller.roles
  // is the source of truth post-Commit C and already incorporates the
  // Cognito-group → role mapping on the server side.
  if (callerRoles.length === 0) {
    for (const g of cognitoGroups) {
      const mapped = mapCognitoGroup(g);
      if (mapped) roles.add(mapped);
    }
  }
  // Augment with ProgramAdmin derived from any active simpl-api
  // programMembership. See JSDoc above for why this is needed.
  for (const m of programMemberships) {
    if (!m) continue;
    if (m.endDate) continue;
    if (m.programRole === "ProgramAdmin") {
      roles.add(Role.ProgramAdmin);
    }
  }
  return Array.from(roles);
}
